import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Form, InputGroup, Modal, Spinner, Tab, Table, Tabs } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import apiCall from "../../API/API";
import ExportButtons, { collectPages } from "../../Component/ExportButtons/ExportButtons";
import "../PlanSettings/PlanSettings.css";
import "../ActivationRequests/ActivationRequests.css";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmt = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "");
const EXPORT_COLUMNS = [
  { header: "#", value: (r) => r.Id },
  { header: "Name", value: (r) => r.name },
  { header: "Username", value: (r) => r.username },
  { header: "Mobile", value: (r) => r.mobile },
  { header: "Reward", value: (r) => r.reward_name },
  { header: "Reward value", value: (r) => inr(r.reward_amount) },
  { header: "Reward item", value: (r) => r.reward_item },
  { header: "Achieved on", value: (r) => fmt(r.achieved_at) },
  { header: "Settled as", value: (r) => (r.settlement?.type === "cash" ? `Cash ${inr(r.settlement.amount)}` : r.settlement?.type === "item" ? `Item: ${r.settlement.item}` : "Not settled") },
  { header: "Settled on", value: (r) => fmt(r.settlement?.at) },
  { header: "Settled by", value: (r) => r.settlement?.by?.username || r.settlement?.by || "" },
  { header: "Note", value: (r) => r.settlement?.note },
];
const TABS = [
  { key: "pending", title: "To settle" },
  { key: "settled", title: "Settled" },
  { key: "all", title: "All" },
];
const EMPTY = { search: "", rankId: "", from: "", to: "" };

/** Pay the reward as cash or record the item given. */
const SettleModal = ({ row, type, onClose, onDone }) => {
  const [amount, setAmount] = useState("");
  const [item, setItem] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (row) {
      setAmount(row.reward_amount || "");
      setItem(row.reward_item || "");
      setNote("");
    }
  }, [row]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { Id: row.Id, type, note };
    if (type === "cash") body.amount = Number(amount);
    else body.item = item;
    const { data, error } = await apiCall("POST", "/reward-achievements/settle", body);
    setSaving(false);
    if (error) return toast.error(error.message || "Could not settle the reward.");
    toast.success(data?.message || "Settled.");
    onDone();
  };

  return (
    <Modal show={!!row} onHide={onClose} centered className="plan-settings-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>{type === "cash" ? "Pay reward as cash" : "Give reward item"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {row && (
            <>
              <div className="ar-member-card">
                <span className="ar-avatar">{String(row.name || row.username || "?").slice(0, 2).toUpperCase()}</span>
                <div>
                  <div className="ps-strong">{row.name || row.username}</div>
                  <div className="ps-muted ps-small">{row.username} · achieved {moment(row.achieved_at).format("DD MMM YYYY")}</div>
                </div>
                <div className="ar-member-amount">
                  <div className="ps-strong">{row.reward_name}</div>
                  <div className="ps-muted ps-small">{inr(row.reward_amount)} · {row.reward_item || "—"}</div>
                </div>
              </div>

              {type === "cash" ? (
                <Form.Group className="mt-3">
                  <Form.Label>Cash amount</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>₹</InputGroup.Text>
                    <Form.Control type="number" min="1" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} required />
                  </InputGroup>
                  <Form.Text>Credited to the member's Reward Income now; it goes out with their next payout.</Form.Text>
                </Form.Group>
              ) : (
                <Form.Group className="mt-3">
                  <Form.Label>Item given</Form.Label>
                  <Form.Control value={item} onChange={(e) => setItem(e.target.value)} maxLength={200} required />
                  <Form.Text>Nothing is credited; the member sees this item as their reward.</Form.Text>
                </Form.Group>
              )}

              <Form.Group className="mt-3">
                <Form.Label>Note (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  maxLength={300}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={type === "cash" ? "e.g. Paid as cash instead of the mobile" : "e.g. Delivered on 12 Oct by courier"}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant={type === "cash" ? "success" : "primary"} disabled={saving}>
            {saving ? <Spinner size="sm" /> : type === "cash" ? "Credit cash" : "Mark item given"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const RewardAchievers = () => {
  const [tab, setTab] = useState("pending");
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [settling, setSettling] = useState(null); // { row, type }
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ status: tab, page, limit });
    Object.entries(applied).forEach(([k, v]) => v && params.set(k, v));
    const { data, error } = await apiCall("GET", `/reward-achievements?${params.toString()}`);
    setLoading(false);
    if (error) return toast.error(error.message || "Could not load reward achievers.");
    setRows(data?.data || []);
    setRewards(data?.rewards || []);
    setSummary(data?.summary || null);
    setPages(data?.pages || 1);
    setTotal(data?.total || 0);
  }, [tab, applied, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setApplied(filters);
    }, 350);
    return () => clearTimeout(t);
  }, [filters]);

  const scan = async () => {
    setScanning(true);
    const { data, error } = await apiCall("POST", "/reward-achievements/scan");
    setScanning(false);
    if (error) return toast.error(error.message || "Could not check for achievers.");
    toast.success(data?.message || "Checked.");
    load();
  };

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const active = Object.values(applied).filter(Boolean).length;

  return (
    <div className="plan-settings activation-requests-page">
      <div className="ps-header">
        <div>
          <h2>Reward Achievers</h2>
          <p>Members who reached a reward. Pay each one as cash (into Reward Income) or give the reward item; the member sees it on their Rewards page.</p>
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <ExportButtons
            title={`Reward Achievers - ${TABS.find((t) => t.key === tab)?.title || ""}`}
            columns={EXPORT_COLUMNS}
            filters={Object.entries(applied).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(", ")}
            getRows={() =>
              collectPages(async (p, l) => {
                const params = new URLSearchParams({ status: tab, page: p, limit: l });
                Object.entries(applied).forEach(([k, v]) => v && params.set(k, v));
                const { data } = await apiCall("GET", `/reward-achievements?${params.toString()}`);
                return { rows: data?.data || [], pages: data?.pages || 1 };
              })
            }
          />
          <Button className="ar-primary-btn" onClick={scan} disabled={scanning}>
            {scanning ? <Spinner size="sm" /> : "Check for new achievers"}
          </Button>
        </div>
      </div>

      <div className="ps-stats ar-stats">
        <button type="button" className={`ps-stat ar-stat ar-stat--pending${tab === "pending" ? " ar-stat--active" : ""}`} onClick={() => { setTab("pending"); setPage(1); }}>
          <span>To settle</span>
          <strong>{summary?.pending ?? 0}</strong>
          <small>waiting for your decision</small>
        </button>
        <button type="button" className={`ps-stat ar-stat ar-stat--approved${tab === "settled" ? " ar-stat--active" : ""}`} onClick={() => { setTab("settled"); setPage(1); }}>
          <span>Paid as cash</span>
          <strong>{summary?.cash ?? 0}</strong>
          <small>{inr(summary?.cash_amount)} credited</small>
        </button>
        <button type="button" className={`ps-stat ar-stat${tab === "settled" ? " ar-stat--active" : ""}`} onClick={() => { setTab("settled"); setPage(1); }}>
          <span>Items given</span>
          <strong>{summary?.item ?? 0}</strong>
          <small>reward items</small>
        </button>
      </div>

      <Tabs activeKey={tab} onSelect={(k) => { setTab(k); setPage(1); }} className="ps-tabs">
        {TABS.map((t) => (
          <Tab eventKey={t.key} title={t.title} key={t.key}>
            <div className="ps-panel">
              <div className="ar-panel-title">
                <h3>{t.key === "pending" ? "Achieved, not settled yet" : t.key === "settled" ? "Settled rewards" : "All achievements"}</h3>
                <span className="ar-count">{total}</span>
                {active > 0 && <span className="ar-filter-badge">{active} filter{active > 1 ? "s" : ""}</span>}
              </div>

              <div className="ar-filters">
                <div className="ar-f-search">
                  <Form.Label>Search</Form.Label>
                  <Form.Control size="sm" placeholder="Member username or name" value={filters.search} onChange={set("search")} />
                </div>
                <div>
                  <Form.Label>Reward</Form.Label>
                  <Form.Select size="sm" value={filters.rankId} onChange={set("rankId")}>
                    <option value="">All rewards</option>
                    {rewards.map((r) => (
                      <option key={r.rankId} value={r.rankId}>{r.name}</option>
                    ))}
                  </Form.Select>
                </div>
                <div>
                  <Form.Label>Achieved from</Form.Label>
                  <Form.Control size="sm" type="date" value={filters.from} onChange={set("from")} />
                </div>
                <div>
                  <Form.Label>Achieved to</Form.Label>
                  <Form.Control size="sm" type="date" value={filters.to} onChange={set("to")} />
                </div>
                <div className="ar-f-reset">
                  <Button size="sm" variant="outline-secondary" disabled={!active} onClick={() => setFilters(EMPTY)}>Reset</Button>
                </div>
              </div>

              <div className="ps-table-wrap">
                <Table className="ps-table">
                  <thead>
                    <tr>
                      <th>Member</th>
                      <th>Reward</th>
                      <th>Achieved</th>
                      <th>Reward value</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={6} className="ps-empty"><Spinner size="sm" /> Loading…</td></tr>
                    )}
                    {!loading && rows.length === 0 && (
                      <tr><td colSpan={6} className="ps-empty">{t.key === "pending" ? "No rewards waiting. Use \"Check for new achievers\" to look again." : "Nothing here yet."}</td></tr>
                    )}
                    {!loading &&
                      rows.map((r) => (
                        <tr key={r.Id}>
                          <td>
                            <div className="ps-strong">{r.name || "—"}</div>
                            <div className="ps-muted ps-small">{r.username}{r.mobile ? ` · ${r.mobile}` : ""}</div>
                          </td>
                          <td className="ps-strong">{r.reward_name}</td>
                          <td className="ps-small">{moment(r.achieved_at).format("DD MMM YYYY")}</td>
                          <td>
                            <div className="ps-strong">{inr(r.reward_amount)}</div>
                            {r.reward_item && <div className="ps-muted ps-small">{r.reward_item}</div>}
                          </td>
                          <td>
                            {r.status === "pending" && <Badge bg="warning" text="dark">To settle</Badge>}
                            {r.status === "processing" && <Badge bg="secondary">Processing</Badge>}
                            {r.settlement?.type === "cash" && (
                              <>
                                <Badge bg="success">Cash {inr(r.settlement.amount)}</Badge>
                                <div className="ps-muted ps-small">{r.settlement.by} · {moment(r.settlement.at).format("DD MMM YYYY")}</div>
                              </>
                            )}
                            {r.settlement?.type === "item" && (
                              <>
                                <Badge bg="primary">Item given</Badge>
                                <div className="ps-muted ps-small">{r.settlement.item}</div>
                                <div className="ps-muted ps-small">{r.settlement.by} · {moment(r.settlement.at).format("DD MMM YYYY")}</div>
                              </>
                            )}
                            {r.settlement?.note && <div className="ps-muted ps-small">“{r.settlement.note}”</div>}
                          </td>
                          <td className="text-end">
                            {r.status === "pending" ? (
                              <div className="ar-actions">
                                <Button size="sm" variant="success" onClick={() => setSettling({ row: r, type: "cash" })}>Pay cash</Button>
                                <Button size="sm" variant="outline-primary" onClick={() => setSettling({ row: r, type: "item" })}>Give item</Button>
                              </div>
                            ) : (
                              <span className="ps-muted ps-small">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>

              <div className="ar-pagination">
                <div className="ar-pagination-info">
                  Showing <strong>{total ? (page - 1) * limit + 1 : 0}–{Math.min(page * limit, total)}</strong> of <strong>{total}</strong>
                </div>
                <div className="ar-pages">
                  <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
                  <span className="ps-muted ps-small">Page {page} of {pages}</span>
                  <Button size="sm" variant="outline-secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
                </div>
              </div>
            </div>
          </Tab>
        ))}
      </Tabs>

      <SettleModal
        row={settling?.row}
        type={settling?.type}
        onClose={() => setSettling(null)}
        onDone={() => { setSettling(null); load(); }}
      />
    </div>
  );
};

export default RewardAchievers;
